
import React from 'react';
import { AnimatedSection } from '@/hooks/useScrollAnimation';
import remodelacaoImg from '@/assets/cirurgias-mamarias-especialidade.webp';

interface SpecialtySectionProps {
  onOpenBooking: (procedure: string) => void;
}

const SpecialtySection: React.FC<SpecialtySectionProps> = ({ onOpenBooking }) => {
  return (
    <section id="especialidade" aria-labelledby="specialty-title" className="section-spacing bg-dark-elevated relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] decorative-blur decorative-blur-primary opacity-10" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] decorative-blur decorative-blur-accent opacity-10" aria-hidden="true" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Introdução geral dos procedimentos */}
        <AnimatedSection>
          <header className="text-center mb-16 md:mb-20">
            <p className="subtitle-wide text-gold mb-4 block">Especialidade</p>
            <h2 id="specialty-title" className="text-3xl md:text-4xl lg:text-5xl text-light-primary mb-6 leading-tight tracking-tight">
              Minha especialidade em Cirurgias Mamárias
            </h2>
            <p className="text-sm md:text-base text-light-muted max-w-2xl mx-auto leading-relaxed">
              Planejamento cirúrgico individualizado para melhorar forma, proporção e harmonia das mamas.
            </p>
          </header>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <article className="bg-dark-elevated border border-white/10 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr]">
              {/* Image */}
              <figure className="relative aspect-[4/3] lg:aspect-auto overflow-hidden m-0">
                <div className="h-full lg:absolute lg:inset-0">
                  <picture>
                    <img
                      src={remodelacaoImg}
                      alt="Cirurgias Mamárias"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: '50% 70%' }}
                      loading="lazy"
                      decoding="async"
                      width="640"
                      height="480"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </picture>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-dark-elevated/50 hidden lg:block" aria-hidden="true" />
              </figure>

              {/* Text Content */}
              <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center space-y-6">
                <header>
                  <p className="subtitle-wide text-gold mb-3 block">Especialidade</p>
                  <p className="text-2xl md:text-3xl text-light-primary leading-tight tracking-tight mb-2 font-display" aria-hidden="true">
                    Cirurgias Mamárias
                  </p>
                  <p className="text-sm md:text-base text-light-secondary">
                    Planejamento cirúrgico individualizado para melhorar forma, proporção e harmonia das mamas.
                  </p>
                </header>
                <div className="space-y-4 text-light-muted leading-relaxed text-sm md:text-base">
                  <p>
                    As cirurgias das mamas estão entre os procedimentos mais realizados na cirurgia plástica e exigem avaliação cuidadosa de proporções corporais, características da pele e expectativas de cada paciente.
                  </p>
                  <p>
                    Na minha prática, realizo diferentes abordagens, como mastopexia, mamoplastia de aumento e troca de próteses, sempre com planejamento individualizado e indicação baseada na anatomia e nos objetivos de cada mulher.
                  </p>
                  <p>
                    O objetivo dessas cirurgias é melhorar forma, volume e posicionamento das mamas, tratando situações como flacidez, perda de volume após gestação ou amamentação e assimetrias mamárias.
                  </p>
                  <p>
                    Cada procedimento é planejado com critério técnico, respeitando limites anatômicos e priorizando segurança, naturalidade e estabilidade dos resultados ao longo do tempo.
                  </p>
                  <p>
                    Durante todo o processo, desde a consulta inicial até o acompanhamento pós-operatório, conduzo o tratamento com explicação clara das etapas da cirurgia e acompanhamento próximo da recuperação.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    onClick={() => onOpenBooking('Cirurgias Mamárias')}
                    className="btn-pill btn-gold font-semibold"
                  >
                    Agendar Consulta
                  </button>
                </div>
              </div>
            </div>
          </article>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default SpecialtySection;
